import React from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  IconButton,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  IKit,
  IKitMedida,
  IKitEspessura,
  IKitCor,
  ISelectOption,
} from "../types/kit.types";

interface TabDadosAdicionaisProps {
  kit: Partial<IKit>;
  onChange: (updates: Partial<IKit>) => void;
  coresKit: ISelectOption[];
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f0f0f0",
    borderRadius: 0,
    "& fieldset": {
      border: "none",
    },
  },
};

export const TabDadosAdicionais: React.FC<TabDadosAdicionaisProps> = ({
  kit,
  onChange,
  coresKit,
}) => {
  const tipoEngenharia = kit.tipo === "Engenharia";

  const handleMedidaChange = (index: number, campo: keyof IKitMedida, valor: any) => {
    const medidas = [...(kit.medidas || [])];
    medidas[index] = { ...medidas[index], [campo]: valor };
    onChange({ medidas });
  };

  const handleAdicionarMedida = () => {
    const medidas = [...(kit.medidas || [])];
    medidas.push({ larguraMm: undefined, alturaMm: undefined, ordem: medidas.length });
    onChange({ medidas });
  };

  const handleRemoverMedida = (index: number) => {
    const medidas = [...(kit.medidas || [])];
    medidas.splice(index, 1);
    onChange({ medidas });
  };

  const handleEspessuraChange = (index: number, valor: number) => {
    const espessuras = [...(kit.espessuras || [])];
    espessuras[index] = { ...espessuras[index], espessura: valor, ordem: index };
    onChange({ espessuras });
  };

  const handleAdicionarEspessura = () => {
    const espessuras = [...(kit.espessuras || [])];
    espessuras.push({ espessura: 0, ordem: espessuras.length });
    onChange({ espessuras });
  };

  const handleRemoverEspessura = (index: number) => {
    const espessuras = [...(kit.espessuras || [])];
    espessuras.splice(index, 1);
    onChange({ espessuras });
  };

  const handleCorChange = (index: number, idCor: number) => {
    const cores = [...(kit.cores || [])];
    cores[index] = { ...cores[index], idCor, ordem: index };
    onChange({ cores });
  };

  const handleAdicionarCor = () => {
    const cores = [...(kit.cores || [])];
    cores.push({ idCor: 0, ordem: cores.length });
    onChange({ cores });
  };

  const handleRemoverCor = (index: number) => {
    const cores = [...(kit.cores || [])];
    cores.splice(index, 1);
    onChange({ cores });
  };

  // FASE 05 — 4ª validação/Frente 9 2026-07-30: Largura/Altura passam a aceitar até
  // 5 caracteres (99999); o limite de 3 (999) permanece só para Espessura.
  const validarNumero = (valor: string, max: number = 999): number => {
    const num = parseInt(valor);
    if (isNaN(num) || num < 0) return 0;
    if (num > max) return max;
    return num;
  };

  return (
    <Grid container spacing={3}>
      {/* Painel 1: Largura e Altura (Kit Engenharia) */}
      {tipoEngenharia && (
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Largura e Altura (Kit Engenharia)</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAdicionarMedida}
                size="small"
                variant="outlined"
              >
                ADICIONAR MEDIDAS +
              </Button>
            </Box>
            <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
              {(kit.medidas || []).map((medida, index) => (
                <Box key={index} sx={{ mb: 2, p: 1, border: "1px solid #e0e0e0" }}>
                  <Box display="flex" gap={1} alignItems="center">
                    <TextField
                      label="Largura (em mm)"
                      type="number"
                      value={medida.larguraMm || ""}
                      onChange={(e) =>
                        handleMedidaChange(index, "larguraMm", validarNumero(e.target.value, 99999))
                      }
                      inputProps={{ min: 0, max: 99999 }}
                      size="small"
                      sx={{ flex: 1, ...inputSx }}
                    />
                    <TextField
                      label="Altura (em mm)"
                      type="number"
                      value={medida.alturaMm || ""}
                      onChange={(e) =>
                        handleMedidaChange(index, "alturaMm", validarNumero(e.target.value, 99999))
                      }
                      inputProps={{ min: 0, max: 99999 }}
                      size="small"
                      sx={{ flex: 1, ...inputSx }}
                    />
                    <IconButton
                      onClick={() => handleRemoverMedida(index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
              {(!kit.medidas || kit.medidas.length === 0) && (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  Nenhuma medida cadastrada
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      )}

      {/* Painel 2: Espessuras */}
      <Grid item xs={12} md={tipoEngenharia ? 4 : 6}>
        <Paper sx={{ p: 2, height: "100%" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Espessuras</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAdicionarEspessura}
              size="small"
              variant="outlined"
            >
              ADICIONAR ESPESSURAS +
            </Button>
          </Box>
          <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
            {(kit.espessuras || []).map((espessura, index) => (
              <Box key={index} sx={{ mb: 2, p: 1, border: "1px solid #e0e0e0" }}>
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    label="Espessura"
                    type="number"
                    value={espessura.espessura || ""}
                    onChange={(e) => handleEspessuraChange(index, validarNumero(e.target.value))}
                    inputProps={{ min: 0, max: 999 }}
                    size="small"
                    fullWidth
                    sx={inputSx}
                  />
                  <IconButton
                    onClick={() => handleRemoverEspessura(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            ))}
            {(!kit.espessuras || kit.espessuras.length === 0) && (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                Nenhuma espessura cadastrada
              </Typography>
            )}
          </Box>
        </Paper>
      </Grid>

      {/* Painel 3: Cores do kit */}
      <Grid item xs={12} md={tipoEngenharia ? 4 : 6}>
        <Paper sx={{ p: 2, height: "100%" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Cores do kit</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAdicionarCor}
              size="small"
              variant="outlined"
            >
              ADICIONAR COR +
            </Button>
          </Box>
          <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
            {(kit.cores || []).map((cor, index) => {
              const coresSelecionadas = (kit.cores || [])
                .map((c) => c.idCor)
                .filter((id) => id > 0);
              const coresDisponiveis = coresKit.filter(
                (c) => c.id === cor.idCor || !coresSelecionadas.includes(c.id)
              );

              return (
                <Box key={index} sx={{ mb: 2, p: 1, border: "1px solid #e0e0e0" }}>
                  <Box display="flex" gap={1} alignItems="center">
                    <FormControl fullWidth size="small" sx={inputSx}>
                      <InputLabel>Cor</InputLabel>
                      <Select
                        value={cor.idCor || ""}
                        onChange={(e) => handleCorChange(index, e.target.value as number)}
                        label="Cor"
                      >
                        <MenuItem value={0}>Selecione uma cor...</MenuItem>
                        {coresDisponiveis.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.nome}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      onClick={() => handleRemoverCor(index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
            {(!kit.cores || kit.cores.length === 0) && (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                {coresKit.length === 0
                  ? "Nenhum item disponível. Verifique os cadastros correspondentes."
                  : "Nenhuma cor cadastrada"}
              </Typography>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};
